import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Users, Shield, Trash2, UserPlus, Eye, Crown, Utensils, UserCheck, List, Search, Ticket, Armchair, Wallet, Edit, X, Check, RefreshCw, FileText, Home, ChevronDown, Radio } from 'lucide-react';

// Role categories for better organization
const ROLE_CATEGORIES = [
  {
    name: 'Admin Roles',
    description: 'Full system access',
    roles: ['super_admin', 'admin']
  },
  {
    name: 'Family & Member Access',
    description: 'View families and members',
    roles: ['family_viewer', 'masool', 'masoola', 'musaid', 'musaida']
  },
  {
    name: 'FMB Access',
    description: 'FMB Thaali management',
    roles: ['fmb_viewer', 'zabhiyat']
  },
  {
    name: 'Events & Tokens',
    description: 'Event management',
    roles: ['event_incharge', 'khidmat_guzar', 'token_manager']
  },
  {
    name: 'Forms & Lists',
    description: 'Forms and custom lists',
    roles: ['forms_manager', 'forms_viewer', 'list_manager']
  },
  {
    name: 'Other Roles',
    description: 'Specialized access',
    roles: ['wajebaat', 'seat_management', 'toloba_collector', 'live_stream_manager', 'mumineen']
  }
];

const ROLES = [
  // Admin Roles
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-700', icon: Crown, description: 'Full access + User management', canExportPdf: true, canExportExcel: true, category: 'Admin Roles' },
  { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-700', icon: Shield, description: 'Full access to all features', canExportPdf: true, canExportExcel: true, category: 'Admin Roles' },
  
  // Family & Member Access
  { value: 'family_viewer', label: 'Family Viewer', color: 'bg-sky-100 text-sky-700', icon: Home, description: 'View families only (no edit)', canExportPdf: false, canExportExcel: false, category: 'Family & Member Access' },
  { value: 'masool', label: 'Masool', color: 'bg-amber-100 text-amber-700', icon: Users, description: 'Mohalla view + PDF export only', canExportPdf: true, canExportExcel: false, category: 'Family & Member Access' },
  { value: 'masoola', label: 'Masoola', color: 'bg-amber-100 text-amber-700', icon: Users, description: 'Mohalla view + PDF export only', canExportPdf: true, canExportExcel: false, category: 'Family & Member Access' },
  { value: 'musaid', label: 'Musaid', color: 'bg-yellow-100 text-yellow-700', icon: Users, description: 'Sector view + PDF export only', canExportPdf: true, canExportExcel: false, category: 'Family & Member Access' },
  { value: 'musaida', label: 'Musaida', color: 'bg-yellow-100 text-yellow-700', icon: Users, description: 'Sector view + PDF export only', canExportPdf: true, canExportExcel: false, category: 'Family & Member Access' },
  
  // FMB Access
  { value: 'fmb_viewer', label: 'FMB Viewer', color: 'bg-emerald-100 text-emerald-700', icon: Eye, description: 'View-only access to FMB data', canExportPdf: false, canExportExcel: false, category: 'FMB Access' },
  { value: 'zabhiyat', label: 'Zabhiyat', color: 'bg-rose-100 text-rose-700', icon: Users, description: 'Zabhiyat marking in FMB only', canExportPdf: true, canExportExcel: false, category: 'FMB Access' },
  
  // Events & Tokens
  { value: 'event_incharge', label: 'Event Incharge', color: 'bg-violet-100 text-violet-700', icon: UserCheck, description: 'Events & Token Events + PDF', canExportPdf: true, canExportExcel: false, category: 'Events & Tokens' },
  { value: 'khidmat_guzar', label: 'Khidmat Guzar', color: 'bg-blue-100 text-blue-700', icon: UserCheck, description: 'Event distribution access', canExportPdf: false, canExportExcel: false, category: 'Events & Tokens' },
  { value: 'token_manager', label: 'Token Manager', color: 'bg-orange-100 text-orange-700', icon: Ticket, description: 'Token Events access only', canExportPdf: true, canExportExcel: false, category: 'Events & Tokens' },
  
  // Forms & Lists
  { value: 'forms_manager', label: 'Forms Manager', color: 'bg-teal-100 text-teal-700', icon: FileText, description: 'Create forms + View submissions + PDF', canExportPdf: true, canExportExcel: false, category: 'Forms & Lists' },
  { value: 'forms_viewer', label: 'Forms Viewer', color: 'bg-teal-50 text-teal-600', icon: Eye, description: 'View forms & submissions only', canExportPdf: false, canExportExcel: false, category: 'Forms & Lists' },
  { value: 'list_manager', label: 'List Manager', color: 'bg-cyan-100 text-cyan-700', icon: List, description: 'Create & manage own custom lists', canExportPdf: false, canExportExcel: false, category: 'Forms & Lists' },
  
  // Other Roles
  { value: 'wajebaat', label: 'Wajebaat', color: 'bg-indigo-100 text-indigo-700', icon: Wallet, description: 'Wajebaat Sila/Fitra entry + PDF', canExportPdf: true, canExportExcel: false, category: 'Other Roles' },
  { value: 'seat_management', label: 'Seat Management', color: 'bg-pink-100 text-pink-700', icon: Armchair, description: 'Ramzan Seat search only', canExportPdf: false, canExportExcel: false, category: 'Other Roles' },
  { value: 'vaccination_coordinator', label: 'Vaccination Coordinator', color: 'bg-emerald-100 text-emerald-700', icon: Wallet, description: 'Manage Umoor Sehat \u2192 Vaccination Drive (registration, tokens, reports, dashboard)', canExportPdf: true, canExportExcel: true, category: 'Other Roles' },
  { value: 'toloba_collector', label: 'Toloba Collector', color: 'bg-green-100 text-green-700', icon: Wallet, description: 'Toloba collection + Sector list', canExportPdf: true, canExportExcel: false, category: 'Other Roles' },
  { value: 'live_stream_manager', label: 'Live Stream Manager', color: 'bg-fuchsia-100 text-fuchsia-700', icon: Radio, description: 'Full Live Streaming management (streams, OBS, viewers) only', canExportPdf: false, canExportExcel: false, category: 'Other Roles' },
  { value: 'mumineen', label: 'Mumineen', color: 'bg-slate-100 text-slate-700', icon: Users, description: 'View own family only', canExportPdf: false, canExportExcel: false, category: 'Other Roles' },
];

export default function UsersPage() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', full_name: '', role: 'mumineen', roles: [], its_id: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [itsSearch, setItsSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMultiRoleDialog, setShowMultiRoleDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferForm, setTransferForm] = useState({ oldEmail: '', newIts: '' });
  const [transferLoading, setTransferLoading] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
      fetchPendingUsers();
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users.filter(u => u.approved !== false));
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await api.get('/users/pending');
      setPendingUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch pending users');
    }
  };

  const handleItsSearch = async () => {
    if (!itsSearch.trim()) return;
    setSearchLoading(true);
    try {
      const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(itsSearch.trim())}&limit=10`);
      setSearchResults(response.data.members || []);
      if (response.data.members?.length === 0) {
        toast.info('No members found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const payload = { ...createForm };
      
      // Auto-extract ITS from email if it's in format ITS@jamaat.local
      const itsMatch = createForm.email.match(/^(\d{8})@jamaat\.local$/i);
      if (itsMatch) {
        payload.its_id = itsMatch[1];
      } else if (selectedMember?.its_id) {
        payload.its_id = selectedMember.its_id;
      }
      
      // If multiple roles selected, send roles array
      if (createForm.roles && createForm.roles.length > 0) {
        payload.roles = createForm.roles;
        payload.role = createForm.roles[0]; // Primary role for backward compatibility
      }
      await api.post('/users/create', payload);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      setCreateForm({ email: '', password: '', full_name: '', role: 'mumineen', roles: [], its_id: '' });
      setSelectedMember(null);
      setItsSearch('');
      setSearchResults([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleApproveUser = async (userId, approved, role = 'mumineen', roles = []) => {
    try {
      await api.post(`/users/${userId}/approve`, { user_id: userId, approved, role, roles });
      toast.success(approved ? 'User approved successfully' : 'User rejected');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role?role=${newRole}`);
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Handle multi-role update
  const handleMultiRoleUpdate = async () => {
    if (!editingUser) return;
    try {
      const selectedRoles = editingUser.selectedRoles || [];
      if (selectedRoles.length === 0) {
        toast.error('Please select at least one role');
        return;
      }
      await api.put(`/users/${editingUser.id}`, { 
        roles: selectedRoles,
        role: selectedRoles[0] // Primary role
      });
      toast.success('Roles updated successfully');
      setShowMultiRoleDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roles');
    }
  };

  // Open multi-role dialog
  const openMultiRoleDialog = (user) => {
    setEditingUser({
      ...user,
      selectedRoles: user.roles || [user.role] || ['mumineen']
    });
    setShowMultiRoleDialog(true);
  };

  // Toggle role in multi-role selection
  const toggleRole = (roleValue) => {
    if (!editingUser) return;
    const current = editingUser.selectedRoles || [];
    if (current.includes(roleValue)) {
      setEditingUser({
        ...editingUser,
        selectedRoles: current.filter(r => r !== roleValue)
      });
    } else {
      setEditingUser({
        ...editingUser,
        selectedRoles: [...current, roleValue]
      });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Reset all non-admin users
  const handleResetAllUsers = async () => {
    setResetLoading(true);
    setResetResult(null);
    try {
      const response = await api.post('/users/reset-all-non-admins');
      setResetResult(response.data);
      toast.success(`Reset complete: ${response.data.deleted_count} users deleted, ${response.data.leadership_users_created} leadership users created`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset users');
    } finally {
      setResetLoading(false);
    }
  };

  // Sync leadership users from Mohalla/Sector settings
  const handleSyncLeadership = async () => {
    setSyncLoading(true);
    try {
      const response = await api.post('/users/sync-leadership');
      toast.success(`Synced ${response.data.total} leadership users`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync leadership users');
    } finally {
      setSyncLoading(false);
    }
  };

  // Sync ITS IDs from members database and create leadership users
  const handleSyncLeadershipITS = async () => {
    setSyncLoading(true);
    try {
      const response = await api.post('/users/sync-leadership-its');
      const data = response.data;
      toast.success(`Found ${data.total_its_found} ITS IDs, created ${data.users_created?.length || 0} users`);
      
      // Show detailed results
      if (data.mohallas_updated?.length > 0) {
        console.log('Mohallas updated:', data.mohallas_updated);
      }
      if (data.sectors_updated?.length > 0) {
        console.log('Sectors updated:', data.sectors_updated);
      }
      
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync leadership ITS');
    } finally {
      setSyncLoading(false);
    }
  };

  // Transfer lists from old email account to new ITS account
  const handleTransferLists = async () => {
    if (!transferForm.oldEmail || !transferForm.newIts) {
      toast.error('Please fill in both fields');
      return;
    }
    setTransferLoading(true);
    try {
      const response = await api.post(`/users/transfer-lists?old_email=${encodeURIComponent(transferForm.oldEmail)}&new_its=${encodeURIComponent(transferForm.newIts)}`);
      toast.success(`Transferred ${response.data.transferred} lists: ${response.data.lists?.join(', ') || 'None'}`);
      setShowTransferDialog(false);
      setTransferForm({ oldEmail: '', newIts: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer lists');
    } finally {
      setTransferLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES.find(r => r.value === 'mumineen');
    return <Badge className={roleConfig?.color || 'bg-slate-100 text-slate-700'}>{roleConfig?.label || role}</Badge>;
  };

  // Get badges for multiple roles
  const getRolesBadges = (user) => {
    const roles = user.roles || [user.role] || ['mumineen'];
    if (roles.length === 1) {
      return getRoleBadge(roles[0]);
    }
    return (
      <div className="flex flex-wrap gap-1">
        {roles.slice(0, 2).map(r => (
          <Badge key={r} className={`${ROLES.find(role => role.value === r)?.color || 'bg-slate-100'} text-[10px] px-1`}>
            {ROLES.find(role => role.value === r)?.label || r}
          </Badge>
        ))}
        {roles.length > 2 && (
          <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1">+{roles.length - 2}</Badge>
        )}
      </div>
    );
  };

  // Filter users by role (checks both role and roles array)
  const getUsersByRole = (roleFilter) => {
    if (roleFilter === 'all') return users;
    if (roleFilter === 'admins') return users.filter(u => 
      u.role === 'super_admin' || u.role === 'admin' || 
      u.roles?.includes('super_admin') || u.roles?.includes('admin')
    );
    // For other roles, check both primary role and roles array
    return users.filter(u => 
      u.role === roleFilter || 
      u.roles?.includes(roleFilter)
    );
  };

  // Count users by role (checks both role and roles array)
  const getRoleCount = (roleFilter) => {
    if (roleFilter === 'all') return users.length;
    if (roleFilter === 'admins') return users.filter(u => 
      u.role === 'super_admin' || u.role === 'admin' || 
      u.roles?.includes('super_admin') || u.roles?.includes('admin')
    ).length;
    return users.filter(u => 
      u.role === roleFilter || 
      u.roles?.includes(roleFilter)
    ).length;
  };

  // Render user table
  const renderUserTable = (filteredUsers) => (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <Table className="data-table min-w-[600px] sm:min-w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap min-w-[120px]">Name</TableHead>
            <TableHead className="hidden sm:table-cell whitespace-nowrap">Email</TableHead>
            <TableHead className="whitespace-nowrap">Roles</TableHead>
            <TableHead className="hidden md:table-cell whitespace-nowrap">Joined</TableHead>
            <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8">
                <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
              </TableCell>
            </TableRow>
          ) : filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            filteredUsers.map((user) => (
              <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{user.full_name}</p>
                    <p className="text-xs text-slate-500 sm:hidden">{user.email}</p>
                    {user.its_id && <p className="text-xs text-slate-400">ITS: {user.its_id}</p>}
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-slate-600 text-sm">{user.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getRolesBadges(user)}
                    {user.id !== currentUser?.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => openMultiRoleDialog(user)}
                        data-testid={`edit-roles-${user.id}`}
                      >
                        <Edit className="w-3 h-3 text-slate-400 hover:text-navy" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-slate-500 text-sm">
                  {new Date(user.created_at).toLocaleDateString('en-IN')}
                </TableCell>
                <TableCell className="text-right">
                  {user.id !== currentUser?.id && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(user.id)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  // Only Super Admin can access this page
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Shield className="w-16 h-16 text-slate-300" />
        <p className="text-slate-500 text-lg">Super Admin access required</p>
        <p className="text-slate-400 text-sm">Only Super Admins can manage users</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold text-slate-800">User Management</h1>
          <p className="text-xs sm:text-sm text-slate-500">{users.length} users total</p>
        </div>
        
        <div className="flex gap-2 flex-wrap justify-end">
          {/* Transfer Lists Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowTransferDialog(true)}
            data-testid="transfer-lists-btn"
            className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm"
          >
            <List className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Transfer Lists</span>
            <span className="sm:hidden">Transfer</span>
          </Button>
          
          {/* Auto-Sync ITS from Members Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSyncLeadershipITS}
            disabled={syncLoading}
            data-testid="sync-its-btn"
            className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncLoading ? 'Syncing...' : 'Auto-Sync ITS'}</span>
            <span className="sm:hidden">{syncLoading ? '...' : 'Sync ITS'}</span>
          </Button>
          
          {/* Sync Leadership Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSyncLeadership}
            disabled={syncLoading}
            data-testid="sync-leadership-btn"
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-xs sm:text-sm"
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncLoading ? 'Syncing...' : 'Sync Leadership'}</span>
            <span className="sm:hidden">{syncLoading ? '...' : 'Sync'}</span>
          </Button>
          
          {/* Reset All Users Button */}
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={() => setShowResetDialog(true)}
            data-testid="reset-users-btn"
            className="text-xs sm:text-sm"
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Reset All Users</span>
            <span className="sm:hidden">Reset</span>
          </Button>
          
          {/* Create User Button */}
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) {
              setItsSearch('');
              setSearchResults([]);
              setSelectedMember(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-navy hover:bg-navy/90 text-sm" size="sm" data-testid="create-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-navy" />
                Create New User
              </DialogTitle>
            </DialogHeader>
            
            {/* ITS Search Section */}
            <div className="border-b pb-4 mb-4">
              <Label>Search by ITS or Name</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={itsSearch}
                  onChange={(e) => setItsSearch(e.target.value)}
                  placeholder="Enter ITS number or name..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleItsSearch())}
                />
                <Button type="button" variant="outline" onClick={handleItsSearch} disabled={searchLoading}>
                  {searchLoading ? '...' : 'Search'}
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto border rounded">
                  {searchResults.map(member => (
                    <div 
                      key={member.id} 
                      className={`p-2 hover:bg-slate-50 cursor-pointer text-sm border-b last:border-b-0 ${selectedMember?.id === member.id ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setSelectedMember(member);
                        setCreateForm({
                          ...createForm,
                          full_name: member.full_name,
                          email: member.email || `${member.its_id}@jamaat.local`,
                          its_id: member.its_id
                        });
                        setSearchResults([]);
                      }}
                    >
                      <span className="font-mono text-emerald-600">{member.its_id}</span> - {member.full_name}
                    </div>
                  ))}
                </div>
              )}
              {selectedMember && (
                <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                  ✓ Selected: <span className="font-semibold">{selectedMember.full_name}</span> ({selectedMember.its_id})
                </div>
              )}
            </div>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={createForm.full_name}
                  onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email/Username *</Label>
                <Input
                  id="email"
                  type="text"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="Enter email or ITS@jamaat.local"
                  required
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">For ITS login, use: ITS_ID@jamaat.local (e.g., 40401835@jamaat.local)</p>
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Enter password"
                  required
                  className="mt-1"
                  minLength={6}
                />
              </div>
              <div>
                <Label>Roles * (Select one or more)</Label>
                <div className="mt-2 max-h-64 sm:max-h-80 overflow-y-auto border rounded-lg">
                  {ROLE_CATEGORIES.map((category) => (
                    <div key={category.name} className="border-b last:border-b-0">
                      <div className="px-3 py-2 bg-slate-50 sticky top-0">
                        <p className="text-xs font-semibold text-slate-700">{category.name}</p>
                        <p className="text-[10px] text-slate-500">{category.description}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                        {ROLES.filter(r => category.roles.includes(r.value)).map((role) => {
                          const RoleIcon = role.icon || Users;
                          const isSelected = createForm.roles?.includes(role.value);
                          return (
                            <div
                              key={role.value}
                              className={`flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-slate-50 transition-colors ${
                                isSelected 
                                  ? 'border-navy bg-navy/5' 
                                  : 'border-slate-200'
                              }`}
                              onClick={() => {
                                const current = createForm.roles || [];
                                if (current.includes(role.value)) {
                                  const newRoles = current.filter(r => r !== role.value);
                                  setCreateForm({ 
                                    ...createForm, 
                                    roles: newRoles,
                                    role: newRoles[0] || 'mumineen'
                                  });
                                } else {
                                  const newRoles = [...current, role.value];
                                  setCreateForm({ 
                                    ...createForm, 
                                    roles: newRoles,
                                    role: newRoles[0]
                                  });
                                }
                              }}
                              data-testid={`role-option-${role.value}`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-navy border-navy' : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <RoleIcon className="w-3 h-3 flex-shrink-0" />
                                  <span className="text-xs font-medium truncate">{role.label}</span>
                                </div>
                                <p className="text-[10px] text-slate-500 truncate">{role.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {createForm.roles?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {createForm.roles.map(r => {
                      const roleConfig = ROLES.find(role => role.value === r);
                      return (
                        <Badge key={r} className={`${roleConfig?.color || 'bg-slate-100'} text-xs`}>
                          {roleConfig?.label || r}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {/* Note for List Manager */}
                {createForm.roles?.includes('list_manager') && (
                  <div className="mt-3 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-xs text-cyan-800 font-medium">List Manager Note:</p>
                    <p className="text-xs text-cyan-700 mt-1">
                      After creating the user, go to <strong>Custom Lists</strong> → click <strong>"Access"</strong> button on any list → add this user's ITS ID to grant them access to specific lists.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-navy" disabled={createLoading || !createForm.roles?.length}>
                  {createLoading ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <Card className="border-0 shadow-card border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <Shield className="w-5 h-5" />
              Pending Approvals ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg" data-testid={`pending-user-${user.id}`}>
                  <div>
                    <p className="font-medium text-slate-800">{user.full_name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                    <p className="text-xs text-slate-400">Registered: {new Date(user.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select onValueChange={(role) => handleApproveUser(user.id, true, role)}>
                      <SelectTrigger className="w-36" data-testid={`approve-role-${user.id}`}>
                        <SelectValue placeholder="Approve as..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map(role => (
                          <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      onClick={() => handleApproveUser(user.id, false)}
                      data-testid={`reject-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Legend - Categorized & Mobile Friendly */}
      <Card className="border-0 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-navy" />
            Role Permissions Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <div className="space-y-3">
            {ROLE_CATEGORIES.map(category => (
              <div key={category.name} className="border rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b">
                  <p className="text-xs sm:text-sm font-semibold text-slate-700">{category.name}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">{category.description}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 p-2">
                  {ROLES.filter(r => category.roles.includes(r.value)).map(role => {
                    const RoleIcon = role.icon || Users;
                    return (
                      <div key={role.value} className={`p-2 rounded-lg ${role.color.replace('text-', 'bg-').replace('-700', '-50').replace('-100', '-50').replace('-600', '-50')}`}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <RoleIcon className="w-3 h-3 flex-shrink-0" />
                          <p className="font-medium text-[10px] sm:text-xs truncate">{role.label}</p>
                        </div>
                        <p className="text-[9px] sm:text-[10px] opacity-80 line-clamp-2">{role.description}</p>
                        <div className="flex gap-1 mt-1">
                          {role.canExportPdf && <span className="text-[8px] px-1 bg-white/50 rounded">PDF</span>}
                          {role.canExportExcel && <span className="text-[8px] px-1 bg-white/50 rounded">Excel</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users Table with Tabs */}
      <Card className="border-0 shadow-card overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0 px-2 sm:px-6">
            <TabsList className="w-full justify-start flex-wrap h-auto gap-1 bg-slate-100 p-1 text-xs sm:text-sm">
              <TabsTrigger value="all" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                All ({getRoleCount('all')})
              </TabsTrigger>
              <TabsTrigger value="admins" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Admins ({getRoleCount('admins')})
              </TabsTrigger>
              <TabsTrigger value="family_viewer" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Family ({getRoleCount('family_viewer')})
              </TabsTrigger>
              <TabsTrigger value="fmb_viewer" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                FMB ({getRoleCount('fmb_viewer')})
              </TabsTrigger>
              <TabsTrigger value="masool" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Leadership ({getRoleCount('masool')})
              </TabsTrigger>
              <TabsTrigger value="event_incharge" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Events ({getRoleCount('event_incharge')})
              </TabsTrigger>
              <TabsTrigger value="forms_manager" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Forms ({getRoleCount('forms_manager')})
              </TabsTrigger>
              <TabsTrigger value="list_manager" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Lists ({getRoleCount('list_manager')})
              </TabsTrigger>
              <TabsTrigger value="wajebaat" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Wajebaat ({getRoleCount('wajebaat')})
              </TabsTrigger>
              <TabsTrigger value="mumineen" className="data-[state=active]:bg-white px-2 py-1 text-[10px] sm:text-xs">
                Mumineen ({getRoleCount('mumineen')})
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <TabsContent value="all" className="m-0">
                {renderUserTable(getUsersByRole('all'))}
              </TabsContent>
              <TabsContent value="admins" className="m-0">
                {renderUserTable(getUsersByRole('admins'))}
              </TabsContent>
              <TabsContent value="family_viewer" className="m-0">
                {renderUserTable(getUsersByRole('family_viewer'))}
              </TabsContent>
              <TabsContent value="fmb_viewer" className="m-0">
                {renderUserTable(getUsersByRole('fmb_viewer'))}
              </TabsContent>
              <TabsContent value="masool" className="m-0">
                {renderUserTable(getUsersByRole('masool'))}
              </TabsContent>
              <TabsContent value="event_incharge" className="m-0">
                {renderUserTable(getUsersByRole('event_incharge'))}
              </TabsContent>
              <TabsContent value="forms_manager" className="m-0">
                {renderUserTable(getUsersByRole('forms_manager'))}
              </TabsContent>
              <TabsContent value="list_manager" className="m-0">
                {renderUserTable(getUsersByRole('list_manager'))}
              </TabsContent>
              <TabsContent value="wajebaat" className="m-0">
                {renderUserTable(getUsersByRole('wajebaat'))}
              </TabsContent>
              <TabsContent value="mumineen" className="m-0">
                {renderUserTable(getUsersByRole('mumineen'))}
              </TabsContent>
            </div>
          </CardContent>
        </Tabs>
      </Card>

      {/* Multi-Role Edit Dialog */}
      <Dialog open={showMultiRoleDialog} onOpenChange={setShowMultiRoleDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Roles for {editingUser?.full_name}
            </DialogTitle>
            <DialogDescription>
              Select multiple roles for this user. Permissions will be merged (additive).
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[50vh] overflow-y-auto border rounded-lg">
            {ROLE_CATEGORIES.map((category) => (
              <div key={category.name} className="border-b last:border-b-0">
                <div className="px-3 py-2 bg-slate-50 sticky top-0 z-10">
                  <p className="text-xs font-semibold text-slate-700">{category.name}</p>
                  <p className="text-[10px] text-slate-500">{category.description}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                  {ROLES.filter(r => category.roles.includes(r.value)).map((role) => {
                    const RoleIcon = role.icon || Users;
                    const isSelected = editingUser?.selectedRoles?.includes(role.value);
                    return (
                      <div
                        key={role.value}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors ${
                          isSelected 
                            ? 'border-navy bg-navy/5' 
                            : 'border-slate-200'
                        }`}
                        onClick={() => toggleRole(role.value)}
                        data-testid={`edit-role-option-${role.value}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-navy border-navy' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <RoleIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="text-xs font-medium truncate">{role.label}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 truncate">{role.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          {editingUser?.selectedRoles?.length > 0 && (
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-slate-600 mb-2">Selected Roles ({editingUser.selectedRoles.length}):</p>
              <div className="flex flex-wrap gap-1">
                {editingUser.selectedRoles.map(r => {
                  const roleConfig = ROLES.find(role => role.value === r);
                  return (
                    <Badge key={r} className={`${roleConfig?.color || 'bg-slate-100'} text-xs`}>
                      {roleConfig?.label || r}
                    </Badge>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                PDF Export: {editingUser.selectedRoles.some(r => ROLES.find(role => role.value === r)?.canExportPdf) ? '✅' : '❌'} | 
                Excel Export: {editingUser.selectedRoles.some(r => ROLES.find(role => role.value === r)?.canExportExcel) ? '✅' : '❌'}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowMultiRoleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMultiRoleUpdate}
              disabled={!editingUser?.selectedRoles?.length}
              className="bg-navy hover:bg-navy/90"
            >
              <Check className="w-4 h-4 mr-1" />
              Save Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset All Users Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => {
        setShowResetDialog(open);
        if (!open) setResetResult(null);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Reset All Non-Admin Users
            </DialogTitle>
            <DialogDescription className="text-red-500 font-medium">
              Warning: This action is irreversible!
            </DialogDescription>
          </DialogHeader>
          
          {!resetResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="font-medium text-red-800 mb-2">This will:</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>Delete ALL users except admins</li>
                  <li>Recreate leadership users (Masool, Masoola, Musaid, Musaida) from Mohalla/Sector settings</li>
                  <li>Reset all permissions to default</li>
                </ul>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleResetAllUsers}
                  disabled={resetLoading}
                  data-testid="confirm-reset-btn"
                >
                  {resetLoading ? 'Resetting...' : 'Yes, Reset All Users'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-medium text-green-800 mb-2">Reset Complete!</p>
                <ul className="text-sm space-y-1 text-green-700">
                  <li>Deleted: {resetResult.deleted_count} users</li>
                  <li>Admins preserved: {resetResult.admins_preserved}</li>
                  <li>Leadership users created: {resetResult.leadership_users_created}</li>
                </ul>
              </div>
              
              {/* Show mohalla/sector ITS stats */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-1">Mohalla/Sector ITS Stats:</p>
                <ul className="text-blue-700 space-y-0.5">
                  <li>Mohallas found: {resetResult.mohallas_found || 0}</li>
                  <li>With Masool ITS: {resetResult.mohallas_with_masool_its || 0}</li>
                  <li>With Masoola ITS: {resetResult.mohallas_with_masoola_its || 0}</li>
                  <li>Sectors found: {resetResult.sectors_found || 0}</li>
                  <li>With Musaid ITS: {resetResult.sectors_with_musaid_its || 0}</li>
                  <li>With Musaida ITS: {resetResult.sectors_with_musaida_its || 0}</li>
                </ul>
              </div>
              
              {resetResult.note && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  <strong>Note:</strong> {resetResult.note}
                </div>
              )}
              
              {resetResult.created_users?.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded p-2">
                  <p className="font-medium text-sm mb-2">Created Users:</p>
                  {resetResult.created_users.map((user, idx) => (
                    <p key={idx} className="text-xs text-slate-600">{user}</p>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end">
                <Button onClick={() => setShowResetDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Lists Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5 text-purple-600" />
              Transfer Custom Lists
            </DialogTitle>
            <DialogDescription>
              Transfer lists created by an old email account to a new ITS-based account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Old User Email</Label>
              <Input
                placeholder="e.g., 30445237@gmail.com"
                value={transferForm.oldEmail}
                onChange={(e) => setTransferForm({...transferForm, oldEmail: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>New User ITS ID</Label>
              <Input
                placeholder="e.g., 30445237"
                value={transferForm.newIts}
                onChange={(e) => setTransferForm({...transferForm, newIts: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTransferLists} 
              disabled={transferLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {transferLoading ? 'Transferring...' : 'Transfer Lists'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
